import React, { useCallback, useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { FaPlus, FaSearch, FaEye, FaBookOpen } from 'react-icons/fa';
import { BiPencil } from 'react-icons/bi';
import client from '../../api/client';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { getVisitStatusBadgeClass, mapAdminVisitFromApi } from '../../Utils/adminVisits';
import AdminVisitFormModal from '../../components/admin/AdminVisitFormModal';
import AdminAssignPublishModal from '../../components/admin/AdminAssignPublishModal';

const STATUS_TABS = [
  { key: 'all', label: 'All' },
  { key: 'Submitted', label: 'Submitted' },
  { key: 'Published', label: 'Published' },
  { key: 'Assigned', label: 'Assigned' },
  { key: 'Completed', label: 'Completed' },
  { key: 'Paid', label: 'Paid & Closed' },
  { key: 'Expired', label: 'Expired' },
  { key: 'Cancelled', label: 'Cancelled' },
];

function AdminVisitRequests() {
  const { user } = useAuth();
  const adminUserId = user?.userId || 1;

  const [visits, setVisits] = useState([]);
  const [patients, setPatients] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);

  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [districtFilter, setDistrictFilter] = useState('');
  const [serviceFilter, setServiceFilter] = useState('');

  const [showViewModal, setShowViewModal] = useState(false);
  const [showAssignPublishModal, setShowAssignPublishModal] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [editVisit, setEditVisit] = useState(null);

  const loadLookups = useCallback(async () => {
    try {
      const [patRes, distRes, svcRes, docRes] = await Promise.all([
        client.get('/api/admin/patients'),
        client.get('/api/districts'),
        client.get('/api/service-types'),
        client.get('/api/admin/doctors'),
      ]);
      if (patRes.data?.success) setPatients(patRes.data.data || []);
      if (distRes.data?.success) setDistricts(distRes.data.data || []);
      if (svcRes.data?.success) setServiceTypes(svcRes.data.data || []);
      if (docRes.data?.success) setDoctors(docRes.data.data || []);
    } catch {
      /* optional */
    }
  }, []);

  const loadVisits = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.get('/api/admin/visit-requests', {
        params: {
          status: activeTab === 'all' ? undefined : activeTab,
          search: searchTerm || undefined,
          districtId: districtFilter || undefined,
          serviceTypeId: serviceFilter || undefined,
        },
      });
      if (res.data?.success) {
        setVisits((res.data.data || []).map(mapAdminVisitFromApi));
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Unable to load visits.');
    } finally {
      setLoading(false);
    }
  }, [activeTab, searchTerm, districtFilter, serviceFilter]);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  useEffect(() => {
    const t = setTimeout(loadVisits, 300);
    return () => clearTimeout(t);
  }, [loadVisits]);

  const openView = (v) => {
    setSelectedVisit(v);
    setShowViewModal(true);
  };

  const openEdit = (v) => {
    setEditVisit(v);
    setShowFormModal(true);
  };

  const openCreate = () => {
    setEditVisit(null);
    setShowFormModal(true);
  };

  const openAssignPublish = (v) => {
    setSelectedVisit(v);
    setShowAssignPublishModal(true);
  };

  return (
    <>
      <div className="d-block d-md-flex justify-content-between align-items-center mb-4">
        <h2 className="dashboard-title mb-0">Visit Requests</h2>
        <button type="button" className="btn btn-primary" onClick={openCreate}>
          <FaPlus className="me-1" /> New Request
        </button>
      </div>

      <ul className="nav nav-tabs mb-3 flex-nowrap overflow-auto">
        {STATUS_TABS.map((tab) => (
          <li className="nav-item" key={tab.key}>
            <button
              type="button"
              className={`nav-link${activeTab === tab.key ? ' active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          </li>
        ))}
      </ul>

      <div className="mb-4">
        <div className="row align-items-end">
          <div className="col-md-4 mb-3 mb-md-0">
            <label htmlFor="visitSearch" className="form-label">
              Search
            </label>
            <div className="input-group">
              <span className="input-group-text">
                <FaSearch />
              </span>
              <input
                type="text"
                className="form-control"
                id="visitSearch"
                placeholder="Patient, booking..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="col-md-4 mb-3 mb-md-0">
            <label htmlFor="visitDistrict" className="form-label">
              District
            </label>
            <select
              className="form-select"
              id="visitDistrict"
              value={districtFilter}
              onChange={(e) => setDistrictFilter(e.target.value)}
            >
              <option value="">All Districts</option>
              {districts.map((d) => (
                <option key={d.idDistrict} value={d.idDistrict}>
                  {d.districtName}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-4 mb-3 mb-md-0">
            <label htmlFor="visitService" className="form-label">
              Service
            </label>
            <select
              className="form-select"
              id="visitService"
              value={serviceFilter}
              onChange={(e) => setServiceFilter(e.target.value)}
            >
              <option value="">All Services</option>
              {serviceTypes.map((s) => (
                <option key={s.idServiceType} value={s.idServiceType}>
                  {s.serviceName}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover">
              <thead className="table-light">
                <tr>
                  <th scope="col">Booking</th>
                  <th scope="col">Patient</th>
                  <th scope="col">Service</th>
                  <th scope="col">Pref. Date</th>
                  <th scope="col">Pref. Time</th>
                  <th scope="col">District/PIN</th>
                  <th scope="col">Doctor Rate</th>
                  <th scope="col">Status</th>
                  <th scope="col">Payment</th>
                  <th scope="col" className="text-center" style={{ width: '100px' }}>
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={10} className="text-center">
                      Loading...
                    </td>
                  </tr>
                )}
                {!loading && visits.length === 0 && (
                  <tr>
                    <td colSpan={10} className="text-center">
                      No visit requests found
                    </td>
                  </tr>
                )}
                {!loading &&
                  visits.map((v) => (
                    <tr key={v.id}>
                      <td>{v.bookingNumber}</td>
                      <td className="fw-bold">
                        <button
                          type="button"
                          className="btn btn-link p-0 text-primary text-decoration-none"
                          onClick={() => openView(v)}
                        >
                          {v.patientName}
                        </button>
                      </td>
                      <td>{v.serviceTypeName}</td>
                      <td>{v.preferredDate}</td>
                      <td>{v.preferredTime}</td>
                      <td>
                        {[v.districtName, v.pinCode].filter(Boolean).join(' / ') || '-'}
                      </td>
                      <td>{v.doctorRateLabel}</td>
                      <td>
                        <span className={getVisitStatusBadgeClass(v.status)}>{v.statusLabel}</span>
                      </td>
                      <td>
                        <span className="badge bg-secondary">{v.paymentLabel}</span>
                      </td>
                      <td className="text-center">
                        <div className="d-flex justify-content-center align-items-center gap-1 flex-nowrap visit-action-icons">
                          {(v.canPublish || v.canAssign) && (
                            <button
                              type="button"
                              className="btn btn-link btn-outline-primary p-1 px-2"
                              title={v.canPublish ? 'Assign / Publish' : 'Assign doctor'}
                              onClick={() => openAssignPublish(v)}
                            >
                              <FaBookOpen />
                            </button>
                          )}
                          {v.canEdit && (
                            <button
                              type="button"
                              className="btn btn-link btn-outline-primary p-1 px-2"
                              title="Edit request"
                              onClick={() => openEdit(v)}
                            >
                              <BiPencil />
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn btn-link btn-outline-primary p-1 px-2"
                            title="View details"
                            onClick={() => openView(v)}
                          >
                            <FaEye />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <p className="text-muted small mb-0">
            Status: Submitted → Published → Assigned → Completed → Paid &amp; Closed
          </p>
        </div>
      </div>

      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Visit Request Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedVisit && (
            <div className="row g-3">
              <div className="col-md-6">
                <strong>Booking:</strong> {selectedVisit.bookingNumber}
              </div>
              <div className="col-md-6">
                <strong>Patient:</strong> {selectedVisit.patientName}
              </div>
              <div className="col-12"><hr className="my-1" /></div>
              <div className="col-12">
                <h6 className="mb-2">Patient Contact</h6>
              </div>
              <div className="col-md-6">
                <strong>Phone Number</strong>
                <div>
                  {selectedVisit.patientMobile ? (
                    <a href={`tel:${selectedVisit.patientMobile}`}>{selectedVisit.patientMobile}</a>
                  ) : (
                    '—'
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <strong>Email ID</strong>
                <div className="text-break">
                  {selectedVisit.patientEmail ? (
                    <a href={`mailto:${selectedVisit.patientEmail}`}>{selectedVisit.patientEmail}</a>
                  ) : (
                    '—'
                  )}
                </div>
              </div>
              <div className="col-12">
                <strong>Address</strong>
                <div className="text-break">{selectedVisit.patientAddress || '—'}</div>
              </div>
              {selectedVisit.patientLandmark && (
                <div className="col-12">
                  <strong>Landmark</strong>
                  <div>{selectedVisit.patientLandmark}</div>
                </div>
              )}
              <div className="col-12"><hr className="my-1" /></div>
              <div className="col-md-6">
                <strong>Service:</strong> {selectedVisit.serviceTypeName}
              </div>
              <div className="col-md-6">
                <strong>Preferred:</strong> {selectedVisit.preferredDate}{' '}
                {selectedVisit.preferredTime} IST
              </div>
              <div className="col-md-6">
                <strong>Status:</strong>{' '}
                <span className={getVisitStatusBadgeClass(selectedVisit.status)}>
                  {selectedVisit.statusLabel}
                </span>
              </div>
              <div className="col-md-6">
                <strong>Customer Amount:</strong> {selectedVisit.amount}
              </div>
              <div className="col-md-6">
                <strong>Doctor Rate:</strong> {selectedVisit.doctorRateLabel}
              </div>
              {selectedVisit.assignedDoctorName && (
                <div className="col-md-12">
                  <strong>Doctor:</strong> {selectedVisit.assignedDoctorName}
                </div>
              )}
              <div className="col-md-12">
                <strong>Description:</strong>{' '}
                {selectedVisit.symptoms || selectedVisit.description}
              </div>
              {selectedVisit.additionalNotes && (
                <div className="col-md-12">
                  <strong>Notes:</strong> {selectedVisit.additionalNotes}
                </div>
              )}
              {selectedVisit.cancelReason && (
                <div className="col-md-12 text-danger">
                  <strong>Cancel reason:</strong> {selectedVisit.cancelReason}
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        {selectedVisit && (selectedVisit.canPublish || selectedVisit.canAssign) && (
          <Modal.Footer>
            <button
              type="button"
              className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1"
              onClick={() => {
                setShowViewModal(false);
                openAssignPublish(selectedVisit);
              }}
            >
              <FaBookOpen />
              {selectedVisit.canPublish ? 'Assign / Publish' : 'Assign'}
            </button>
          </Modal.Footer>
        )}
      </Modal>

      <AdminAssignPublishModal
        show={showAssignPublishModal}
        onHide={() => setShowAssignPublishModal(false)}
        visit={selectedVisit}
        doctors={doctors}
        serviceTypes={serviceTypes}
        adminUserId={adminUserId}
        onSuccess={loadVisits}
      />

      <AdminVisitFormModal
        show={showFormModal}
        onHide={() => {
          setShowFormModal(false);
          setEditVisit(null);
        }}
        adminUserId={adminUserId}
        editVisit={editVisit}
        patients={patients}
        onSuccess={loadVisits}
      />
    </>
  );
}

export default AdminVisitRequests;
